const INSTALL_KEY = '__holzcomAuthFetchInstalled';
const handledResponses = new WeakSet<Response>();
export const CRITICAL_API_TIMEOUT_MS = 12_000;
let unauthorizedLatched = false;

function requestPath(input: RequestInfo | URL): string | null {
  try {
    const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const url = new URL(raw, window.location.href);
    return url.origin === window.location.origin ? url.pathname : null;
  } catch {
    return null;
  }
}

export function installAuthFetchInterceptor(): void {
  const state = window as unknown as Record<string, unknown>;
  if (state[INSTALL_KEY]) return;
  const originalFetch = window.fetch.bind(window);
  state[INSTALL_KEY] = originalFetch;
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const response = await originalFetch(input, init);
    handleApiResponse(input, response);
    return response;
  };
}

export function handleApiResponse(input: RequestInfo | URL, response: Response): Response {
  if (handledResponses.has(response)) return response;
  handledResponses.add(response);
  const path = requestPath(input);
  if (response.status === 401 && path?.startsWith('/api/') && !path.startsWith('/api/auth/')) {
    if (!unauthorizedLatched) {
      unauthorizedLatched = true;
      window.dispatchEvent(new Event('holzcom-unauthorized'));
    }
  }
  if (response.status === 409 && path?.startsWith('/api/models/')) {
    window.dispatchEvent(new CustomEvent('holzcom-bundle-conflict', { detail: { path } }));
  }
  return response;
}

export function resetUnauthorizedLatch(): void {
  unauthorizedLatched = false;
}

export async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  return handleApiResponse(input, await fetch(input, init));
}

export type BoundedRequest = { signal: AbortSignal; cleanup: () => void };

export function boundedRequestSignal(caller: AbortSignal | null | undefined, timeoutMs: number): BoundedRequest {
  const deadlineController = new AbortController();
  const timer = window.setTimeout(
    () => deadlineController.abort(new DOMException('request deadline', 'TimeoutError')),
    timeoutMs,
  );
  const deadline = deadlineController.signal;
  const controller = new AbortController();
  const forward = (source: AbortSignal) => {
    if (!controller.signal.aborted) controller.abort(source.reason);
  };
  const forwardCaller = () => forward(caller!);
  const forwardDeadline = () => forward(deadline);
  if (caller?.aborted) forward(caller);
  else caller?.addEventListener('abort', forwardCaller, { once: true });
  if (deadline.aborted) forward(deadline);
  else deadline.addEventListener('abort', forwardDeadline, { once: true });
  let cleaned = false;
  return { signal: controller.signal, cleanup: () => {
    if (cleaned) return;
    cleaned = true;
    window.clearTimeout(timer);
    caller?.removeEventListener('abort', forwardCaller);
    deadline.removeEventListener('abort', forwardDeadline);
  } };
}

export async function criticalApiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const bounded = boundedRequestSignal(init?.signal, CRITICAL_API_TIMEOUT_MS);
  try {
    return handleApiResponse(input, await fetch(input, { ...init, signal: bounded.signal }));
  } finally {
    bounded.cleanup();
  }
}

export async function criticalApiJson<T>(input: RequestInfo | URL, init: RequestInit | undefined,
  decode: (response: Response) => Promise<T>, timeoutMs = CRITICAL_API_TIMEOUT_MS): Promise<T> {
  const bounded = boundedRequestSignal(init?.signal, timeoutMs);
  try {
    const response = await apiFetch(input, { ...init, signal: bounded.signal });
    return await decode(response);
  } finally {
    bounded.cleanup();
  }
}

export function uninstallAuthFetchInterceptorForTests(): void {
  const state = window as unknown as Record<string, unknown>;
  const originalFetch = state[INSTALL_KEY];
  if (typeof originalFetch === 'function') window.fetch = originalFetch as typeof window.fetch;
  delete state[INSTALL_KEY];
  unauthorizedLatched = false;
}
